import { Component, signal, effect, ElementRef, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ProgressBarModule } from 'primeng/progressbar';
import { environment } from '@environ/environment';

/**
 * Loading Splash Component
 *
 * Displays during login/session initialization with:
 * - Animated SVG logo (draw-in effect)
 * - Welcome message
 * - Loading progress bar
 * - Rotating helpful tips
 *
 * Features:
 * - Auto-triggers logo animation on show
 * - Smooth fade out when complete
 * - Theme-aware (uses primary color for logo)
 * - Responsive design
 */
@Component({
  selector: 'loading-splash',
  standalone: true,
  imports: [CommonModule, TranslateModule, ProgressBarModule],
  templateUrl: './loading-splash.component.html',
  styleUrls: ['./loading-splash.component.css']
})
export class LoadingSplashComponent {
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  // Visibility state
  readonly visible = signal(false);

  // Loading progress (0-100)
  readonly progress = signal(0);

  // Current tip index
  readonly currentTipIndex = signal(0);

  // Logo animation state
  readonly logoAnimating = signal(false);

  // Tips to show while loading
  readonly tips = [
    'SPLASH.TIP_LOADING',
    'SPLASH.TIP_FAVORITES',
    'SPLASH.TIP_QUICK_FILTERS',
    'SPLASH.TIP_SEARCH',
    'SPLASH.TIP_VIEW_MODES',
    'SPLASH.TIP_FLAGS'
  ];

  // Version information
  readonly version = environment.version;

  // Tip rotation interval
  private tipInterval?: ReturnType<typeof setInterval>;

  constructor() {
    // Start tip rotation when visible
    effect(() => {
      if (this.visible()) {
        this.startTipRotation();
        // Trigger logo animation after a brief delay
        setTimeout(() => this.logoAnimating.set(true), 100);
      } else {
        this.stopTipRotation();
      }
    }, {  });
  }

  /**
   * Show the splash screen
   */
  show() {
    this.visible.set(true);
    this.progress.set(0);
    this.currentTipIndex.set(0);
  }

  /**
   * Update loading progress
   * @param value Progress percentage (0-100)
   */
  updateProgress(value: number) {
    this.progress.set(Math.min(100, Math.max(0, value)));
  }

  /**
   * Hide the splash screen with fade out animation
   */
  hide() {
    // Trigger fade out animation
    this.logoAnimating.set(false);

    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.visible.set(false);
    }, 700); // Match logo animation duration
  }

  /**
   * Start rotating tips
   */
  private startTipRotation() {
    this.stopTipRotation();

    this.tipInterval = setInterval(() => {
      this.currentTipIndex.update(index =>
        (index + 1) % this.tips.length
      );
    }, 4000); // Change tip every 4 seconds
  }

  /**
   * Stop rotating tips
   */
  private stopTipRotation() {
    if (this.tipInterval) {
      clearInterval(this.tipInterval);
      this.tipInterval = undefined;
    }
  }

  /**
   * Get current tip translation key
   */
  currentTip() {
    return this.tips[this.currentTipIndex()];
  }
}
